import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { BaseModal } from '@/components/modal/BaseModal';
import { useExport, type ExportFormat } from '../../hooks/useExport';
import type { ExportQuality } from '../../types';

const QUALITY_OPTIONS: { value: ExportQuality; labelKey: string; fallback: string }[] = [
  { value: 'best', labelKey: 'editor.qualityBest', fallback: 'Best' },
  { value: '1080p', labelKey: '', fallback: '1080p' },
  { value: '720p', labelKey: '', fallback: '720p' },
];

interface MobileExportSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileExportSheet({ isOpen, onClose }: MobileExportSheetProps) {
  const { t } = useTranslation();
  const { exporting, progress, phase, error, startExport } = useExport();
  const [quality, setQuality] = useState<ExportQuality>('1080p');

  const handleExport = (format: ExportFormat) => {
    startExport(format, quality);
  };

  const getProgressLabel = () => {
    if (phase === 'downloading') return t('editor.downloading', 'Downloading...');
    if (phase === 'mixing') return t('editor.mixing', 'Mixing...');
    return `${t('editor.exporting', 'Exporting...')} ${progress}%`;
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      mobileVariant="bottomsheet"
      ariaLabel={t('editor.export', 'Export')}
      preventClose={exporting}
    >
      <div className="flex flex-col max-h-[80dvh]">
        {/* Drag handle */}
        <div className="flex justify-center py-2 shrink-0">
          <div className="w-8 h-1 rounded-full bg-white/20" />
        </div>

        <div className="px-4 pb-2 shrink-0">
          <h3 className="text-sm font-semibold text-[var(--user-page-text)]">
            {t('editor.export', 'Export')}
          </h3>
        </div>

        <div className="px-4 pb-4 space-y-4">
          {/* Quality selector */}
          <div>
            <div className="text-[10px] text-[#8E8E93] uppercase tracking-wide mb-1.5">
              {t('editor.exportQuality', 'Quality')}
            </div>
            <div className="flex rounded-lg overflow-hidden border border-[#48484A]">
              {QUALITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setQuality(opt.value)}
                  disabled={exporting}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                    quality === opt.value
                      ? 'bg-[var(--user-page-accent)] text-[var(--user-page-bg)]'
                      : 'bg-[#2A2A2E] text-[#E5E5EA] hover:bg-[#3A3A3E]'
                  }`}
                >
                  {opt.labelKey ? t(opt.labelKey, opt.fallback) : opt.fallback}
                </button>
              ))}
            </div>
          </div>

          {/* Export formats */}
          <div className="space-y-2">
            <button
              onClick={() => handleExport('mp4')}
              disabled={exporting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[var(--user-page-accent)] text-[var(--user-page-bg)] font-medium text-sm hover:brightness-110 transition-all disabled:opacity-50"
            >
              <Download size={16} />
              MP4 (Video + Audio)
            </button>
            <button
              onClick={() => handleExport('mp4-video-only')}
              disabled={exporting}
              className="w-full px-4 py-2.5 rounded-lg bg-[#1A1A1E] text-[#E5E5EA] text-sm hover:bg-[#2A2A2E] disabled:opacity-50 transition-colors"
            >
              MP4 (Video Only)
            </button>
            <button
              onClick={() => handleExport('wav')}
              disabled={exporting}
              className="w-full px-4 py-2.5 rounded-lg bg-[#1A1A1E] text-[#E5E5EA] text-sm hover:bg-[#2A2A2E] disabled:opacity-50 transition-colors"
            >
              {t('editor.exportWav', 'WAV (Audio Only)')}
            </button>
          </div>

          {/* Progress */}
          {exporting && (
            <div>
              <div className="text-xs text-[#8E8E93] mb-1">{getProgressLabel()}</div>
              <div className="h-1.5 bg-black/30 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--user-page-accent)] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-400">{error}</p>
          )}
        </div>
      </div>
    </BaseModal>
  );
}
